import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Copy,
  ExternalLink,
  Loader2,
  RotateCcw,
  Search,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  AdminEmptyState,
  AdminLoading,
  AdminPage,
  AdminPanel,
} from "@/components/admin/AdminPrimitives";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import {
  datetimeLocalToIso,
  toDatetimeLocalValue,
} from "@/lib/datetimeLocal";
import { resolveAmazonReportText } from "@/lib/amazonSupportMessage";
import {
  DEFAULT_QUEUE_SORT,
  nextQueueSort,
  sortQueueReviews,
  type QueueSort,
  type QueueSortKey,
} from "@/lib/adminQueueSort";
import {
  actionableQueueReviews,
  actionableViolations,
  batchMessageMatchesSubmission,
  buildBrandRegistrySubmission,
  canonicalReviewUrl,
  formatQueueReviewText,
  groupQueueReviews,
  managedFilingQueueReviews,
  parseBatchMessageProvenance,
  primaryActionableViolation,
  violationLabel,
  type BatchMessageProvenance,
  type QueueViolation,
  type TakedownQueueGroup,
} from "@/lib/adminQueueGroups";

type CustomerStatus = "none" | "invite_sent" | "verified";
type TakedownCapability =
  | "unknown"
  | "unverified_no_brand_registry"
  | "brand_registry_access_required"
  | "brand_registry_access_pending"
  | "brand_registry_ready";

type Violation = QueueViolation;

type QueueReview = {
  id: string;
  asin: string;
  amazonReviewId: string;
  reviewUrl: string | null;
  rating: number;
  title: string | null;
  body: string;
  productTitle: string | null;
  brand: {
    id: string;
    name: string;
    customerId: string;
    organizationName: string;
    brandRegistry: boolean;
    brandRegistryAnsweredAt: string | null;
  } | null;
  customer: {
    id: string;
    email: string;
    organizationName: string;
  } | null;
  classification: {
    violations: Violation[];
    rationale: string | null;
    takedownRequest: string | null;
  };
  auditRequest: { id: string; email: string; createdAt: string };
  customerStatus: CustomerStatus;
  takedownCapability: TakedownCapability;
};

type QueueResponse = {
  filters: {
    asins: string[];
    brand: string;
    email: string;
    customerStatus: string;
    violation: string;
    rating: number | null;
  };
  reviews: QueueReview[];
};

type BatchMessageResponse = {
  message: string;
  source: unknown;
  modelId: unknown;
  category: string;
  asins: string[];
  reviewUrls: string[];
};

type FilterState = {
  asins: string;
  brand: string;
  email: string;
  customerStatus: string;
  rating: string;
  violation: string;
};

const ACCESS_OPTIONS = [
  { value: "all", label: "All access" },
  { value: "verified", label: "Verified" },
  { value: "invite_sent", label: "Invite pending" },
  { value: "none", label: "Not connected" },
];

const RATING_OPTIONS = [
  { value: "all", label: "All ratings" },
  { value: "1", label: "1 star" },
  { value: "2", label: "2 stars" },
  { value: "3", label: "3 stars" },
  { value: "4", label: "4 stars" },
  { value: "5", label: "5 stars" },
];

function parseAsinFilter(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\s,;]+/)
        .map((part) => part.trim().toUpperCase())
        .filter(Boolean)
    )
  ).slice(0, 50);
}

function filtersFromParams(params: URLSearchParams): FilterState {
  return {
    asins: parseAsinFilter(params.get("asins") ?? "").join(", "),
    brand: params.get("brand") ?? "",
    email: params.get("email") ?? "",
    customerStatus: params.get("customerStatus") ?? "all",
    rating: params.get("rating") ?? "all",
    violation: params.get("violation") ?? "",
  };
}

function queryFromFilters(filters: FilterState): string {
  const query = new URLSearchParams();
  const asins = parseAsinFilter(filters.asins);
  if (asins.length) query.set("asins", asins.join(","));
  if (filters.brand.trim()) query.set("brand", filters.brand.trim());
  if (filters.email.trim()) query.set("email", filters.email.trim());
  if (filters.customerStatus && filters.customerStatus !== "all") {
    query.set("customerStatus", filters.customerStatus);
  }
  if (filters.rating && filters.rating !== "all") query.set("rating", filters.rating);
  if (filters.violation.trim()) query.set("violation", filters.violation.trim());
  return query.toString();
}

function TakedownCapabilityPill({
  capability,
}: {
  capability: TakedownCapability;
}) {
  const cls =
    capability === "brand_registry_ready"
      ? "bg-emerald-100 text-emerald-800 ring-emerald-300"
      : capability === "brand_registry_access_pending"
        ? "bg-sky-50 text-sky-700 ring-sky-200"
        : capability === "unverified_no_brand_registry"
          ? "bg-amber-50 text-amber-800 ring-amber-200"
          : "bg-slate-100 text-slate-700 ring-slate-200";
  const label =
    capability === "brand_registry_ready"
      ? "Ready"
      : capability === "brand_registry_access_pending"
        ? "Access pending"
        : capability === "brand_registry_access_required"
          ? "Access required"
          : capability === "unverified_no_brand_registry"
            ? "Unsupported"
            : "Unknown";
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const label = copied ? "Takedown text copied" : "Copy takedown text";

  return (
    <Button
      type="button"
      variant="outline"
      size="icon-sm"
      disabled={!text}
      aria-label={label}
      title={label}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Clipboard can be unavailable in non-secure local contexts.
        }
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function MarkSubmittedButton({
  reviewId,
  onChange,
}: {
  reviewId: string;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="xs"
      disabled={busy}
      aria-label="Mark submitted"
      title="Mark submitted"
      onClick={async () => {
        setBusy(true);
        const res = await api(`/admin/reviews/${reviewId}/takedown-attempts`, {
          method: "POST",
          body: JSON.stringify({
            channel: "public_report",
            notes: "Submitted by operator from the takedown inbox.",
          }),
        });
        setBusy(false);
        if (!res.ok) {
          alert(res.error ?? "Could not mark submitted");
          return;
        }
        onChange();
      }}
    >
      {busy ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <ClipboardCheck className="h-3.5 w-3.5" />
      )}
      <span className="hidden 2xl:inline">Mark submitted</span>
    </Button>
  );
}

function CategoryBatchCard({
  group,
  onSubmitted,
}: {
  group: TakedownQueueGroup<QueueReview>;
  onSubmitted: (message: string) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    group.reviews.map((review) => review.id)
  );
  const [expandedReviewIds, setExpandedReviewIds] = useState<Set<string>>(
    () => new Set()
  );
  const [amazonCaseId, setAmazonCaseId] = useState("");
  const [submittedAt, setSubmittedAt] = useState(() =>
    toDatetimeLocalValue(new Date())
  );
  const [submittedAtEdited, setSubmittedAtEdited] = useState(false);
  const [messageDraft, setMessageDraft] = useState("");
  const [sourceMessage, setSourceMessage] = useState("");
  const [messageProvenance, setMessageProvenance] =
    useState<BatchMessageProvenance | null>(null);
  const [generatedForSelection, setGeneratedForSelection] = useState<
    string | null
  >(null);
  const [copiedField, setCopiedField] = useState<
    "message" | "asins" | "urls" | null
  >(null);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generationRequestRef = useRef(0);
  const reviewSignature = group.reviews.map((review) => review.id).join("|");

  useEffect(() => {
    generationRequestRef.current += 1;
    setSelectedIds(group.reviews.map((review) => review.id));
    setExpandedReviewIds(new Set());
    setSubmittedAt(toDatetimeLocalValue(new Date()));
    setSubmittedAtEdited(false);
    setMessageDraft("");
    setSourceMessage("");
    setMessageProvenance(null);
    setGeneratedForSelection(null);
    setGenerating(false);
    setCopiedField(null);
    setError(null);
  }, [group.key, reviewSignature]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const submission = useMemo(
    () => buildBrandRegistrySubmission(group, selectedIds),
    [group, selectedIds]
  );
  const canonicalReviewIds = submission.ok
    ? submission.reviews.map((review) => review.id)
    : [];
  const selectionKey = JSON.stringify([
    group.key,
    canonicalReviewIds,
    submission.ok ? submission.fields.asins : [],
    submission.ok ? submission.fields.reviewUrls : [],
  ]);
  const currentSelectionKeyRef = useRef(selectionKey);
  currentSelectionKeyRef.current = selectionKey;
  const messageWasGeneratedForCurrentSelection =
    generatedForSelection === selectionKey;
  const messageIsCurrent =
    messageWasGeneratedForCurrentSelection && Boolean(messageDraft.trim());

  useEffect(() => {
    generationRequestRef.current += 1;
    setMessageDraft("");
    setSourceMessage("");
    setMessageProvenance(null);
    setGeneratedForSelection(null);
    setGenerating(false);
    setCopiedField(null);
  }, [selectionKey]);

  const allSelected =
    group.reviews.length > 0 &&
    group.reviews.every((review) => selectedSet.has(review.id));

  function invalidateGeneratedMessage() {
    generationRequestRef.current += 1;
    setMessageDraft("");
    setSourceMessage("");
    setMessageProvenance(null);
    setGeneratedForSelection(null);
    setGenerating(false);
  }

  function toggleReview(reviewId: string, checked: boolean) {
    invalidateGeneratedMessage();
    setSelectedIds((current) =>
      checked
        ? Array.from(new Set([...current, reviewId]))
        : current.filter((id) => id !== reviewId)
    );
    setCopiedField(null);
    setError(null);
  }

  function toggleAll(checked: boolean) {
    invalidateGeneratedMessage();
    setSelectedIds(
      checked ? group.reviews.map((review) => review.id) : []
    );
    setCopiedField(null);
    setError(null);
  }

  function toggleReviewExpansion(reviewId: string) {
    setExpandedReviewIds((current) => {
      const next = new Set(current);
      if (next.has(reviewId)) next.delete(reviewId);
      else next.add(reviewId);
      return next;
    });
  }

  async function generateMessage() {
    if (!submission.ok) return;

    if (
      messageWasGeneratedForCurrentSelection &&
      messageDraft !== sourceMessage &&
      !window.confirm(
        "Replace your edits with a newly generated Amazon support message?"
      )
    ) {
      return;
    }

    const requestId = generationRequestRef.current + 1;
    generationRequestRef.current = requestId;
    const requestedSelectionKey = selectionKey;
    const reviewIds = [...canonicalReviewIds];
    setGenerating(true);
    setCopiedField(null);
    setError(null);

    try {
      const res = await api<BatchMessageResponse>(
        "/admin/takedown-batches/message",
        {
          method: "POST",
          body: JSON.stringify({ reviewIds }),
        }
      );
      if (
        generationRequestRef.current !== requestId ||
        currentSelectionKeyRef.current !== requestedSelectionKey
      ) {
        return;
      }

      if (!res.ok || !res.data) {
        setError(
          res.error ??
            (res.status === 409
              ? "This review selection changed. Refresh the queue and try again."
              : "Could not generate the Amazon support message. Try again.")
        );
        return;
      }

      const responseMatchesVisibleFields = batchMessageMatchesSubmission(
        res.data,
        group.category,
        submission.fields
      );
      if (!responseMatchesVisibleFields) {
        setError(
          "The generated draft did not match the current form fields. Refresh the queue and generate it again."
        );
        return;
      }

      const provenance = parseBatchMessageProvenance(
        res.data.source,
        res.data.modelId
      );
      if (!provenance) {
        setError(
          "The generated draft did not include valid source information. Refresh the queue and generate it again."
        );
        return;
      }

      const nextMessage =
        typeof res.data.message === "string" ? res.data.message.trim() : "";
      if (!nextMessage) {
        setError(
          "The message generator returned an empty draft. Try generating it again."
        );
        return;
      }

      setMessageDraft(nextMessage);
      setSourceMessage(nextMessage);
      setMessageProvenance(provenance);
      setGeneratedForSelection(requestedSelectionKey);
    } catch {
      if (generationRequestRef.current === requestId) {
        setError("Could not generate the Amazon support message. Try again.");
      }
    } finally {
      if (generationRequestRef.current === requestId) {
        setGenerating(false);
      }
    }
  }

  async function copyField(
    field: "message" | "asins" | "urls",
    value: string
  ) {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setError(null);
      window.setTimeout(() => setCopiedField(null), 2000);
    } catch {
      setError("Clipboard access failed. Try copying from a secure browser tab.");
    }
  }

  async function markBatchSubmitted() {
    if (!submission.ok || !messageIsCurrent) return;
    const label = violationLabel(group.category);
    if (
      !window.confirm(
        `Record ${selectedIds.length} ${label} review${
          selectedIds.length === 1 ? "" : "s"
        } as submitted through Brand Registry? Continue only after Amazon accepts the form. Reputely will remove them from this inbox and send the client one summary email. This does not send anything to Amazon.`
      )
    ) {
      return;
    }

    const submittedAtIso = submittedAtEdited
      ? datetimeLocalToIso(submittedAt)
      : new Date().toISOString();
    if (!submittedAtIso) {
      setError("Enter a valid submission date and time.");
      return;
    }

    setBusy(true);
    setError(null);
    const res = await api<{ submittedCount: number }>(
      "/admin/takedown-batches",
      {
        method: "POST",
        body: JSON.stringify({
          reviewIds: canonicalReviewIds,
          channel: "brand_registry",
          message: messageDraft.trim(),
          submittedAt: submittedAtIso,
          ...(amazonCaseId.trim()
            ? { amazonCaseId: amazonCaseId.trim() }
            : {}),
        }),
      }
    );
    setBusy(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not mark this batch submitted.");
      return;
    }

    onSubmitted(
      `Recorded one Brand Registry submission covering ${res.data.submittedCount} review${
        res.data.submittedCount === 1 ? "" : "s"
      } under ${label}.`
    );
  }

  const first = group.reviews[0];
  const owner =
    first.brand?.name ??
    first.customer?.organizationName ??
    first.auditRequest.email;
  const groupAsins = Array.from(
    new Set(group.reviews.map((review) => review.asin.trim().toUpperCase()))
  );
  const formId = group.reviews[0]?.id ?? group.batchNumber;

  return (
    <section className="rounded-lg border border-border bg-background">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border bg-muted/30 px-4 py-3">
        <div className="flex min-w-0 items-start gap-3">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(value) => toggleAll(value === true)}
            disabled={busy || generating}
            aria-label={`Select all ${violationLabel(group.category)} reviews`}
            className="mt-1"
          />
          <div className="min-w-0">
            <h3 className="font-medium text-foreground">
              {violationLabel(group.category)}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {group.reviews.length} review{group.reviews.length === 1 ? "" : "s"}
              {" · "}
              {groupAsins.length} ASIN{groupAsins.length === 1 ? "" : "s"}
              {" · "}
              {owner}
              {group.batchCount > 1 && (
                <>
                  {" · "}Batch {group.batchNumber} of {group.batchCount}
                </>
              )}
            </p>
          </div>
        </div>
        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
          {selectedIds.length} selected
        </span>
      </div>

      <div className="divide-y divide-border">
        {group.reviews.map((review) => {
          const primaryViolation = primaryActionableViolation(review);
          const link = canonicalReviewUrl(review);
          const text = formatQueueReviewText(review);
          const evidence =
            primaryViolation?.evidence?.trim() ||
            "No supporting quote available.";
          const rationale =
            review.classification.rationale?.trim() ||
            "No AI rationale available.";
          const confidence =
            typeof primaryViolation?.confidence === "number"
              ? Math.round(primaryViolation.confidence * 100)
              : null;
          const expanded = expandedReviewIds.has(review.id);
          const reviewTextId = `batch-review-text-${review.id}`;
          const reasoningId = `batch-review-reasoning-${review.id}`;
          return (
            <div
              key={review.id}
              className="grid gap-3 px-4 py-3 sm:grid-cols-[auto_minmax(0,1fr)]"
            >
              <Checkbox
                checked={selectedSet.has(review.id)}
                onCheckedChange={(value) =>
                  toggleReview(review.id, value === true)
                }
                disabled={busy || generating}
                aria-label={`Select Amazon review ${review.amazonReviewId}`}
                className="mt-1"
              />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {link ? (
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-xs font-medium underline-offset-4 hover:underline"
                    >
                      {review.amazonReviewId}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="font-mono text-xs text-muted-foreground">
                      {review.amazonReviewId}
                    </span>
                  )}
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {review.rating}★
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 ring-1 ring-inset ring-red-200">
                    {violationLabel(primaryViolation?.type ?? group.category)}
                    {confidence !== null && (
                      <span className="tabular-nums opacity-70">
                        {confidence}%
                      </span>
                    )}
                  </span>
                </div>
                <p
                  id={reviewTextId}
                  className={`mt-1 break-words text-sm leading-5 text-foreground/80 ${
                    expanded ? "whitespace-pre-wrap" : "line-clamp-2"
                  }`}
                >
                  {text || "No review text"}
                </p>
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={`${reviewTextId} ${reasoningId}`}
                  aria-label={`${
                    expanded ? "Collapse" : "Show full"
                  } review details for Amazon review ${review.amazonReviewId}`}
                  onClick={() => toggleReviewExpansion(review.id)}
                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {expanded ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                  {expanded ? "Show less" : "Show full review"}
                </button>
                <div
                  id={reasoningId}
                  hidden={!expanded}
                  className="mt-3 grid gap-3 rounded-md border border-border bg-muted/30 p-3 text-xs leading-5 text-muted-foreground sm:grid-cols-2"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-foreground/80">
                      Why the AI flagged it
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap break-words">
                      {rationale}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-foreground/80">
                      Supporting quote
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap break-words">
                      {evidence}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-4 border-t border-border bg-muted/20 px-4 py-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-foreground">Amazon subject</span>
          <span className="rounded-md border border-border bg-background px-2.5 py-1 text-muted-foreground">
            Product review violation
          </span>
          <span className="text-xs text-muted-foreground">
            Amazon fills this automatically.
          </span>
        </div>
        <div>
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor={`message-${formId}`}
                className="text-sm font-medium text-foreground"
              >
                Describe the review policy violation
              </label>
              {messageWasGeneratedForCurrentSelection && messageProvenance && (
                <span
                  title={
                    messageProvenance.source === "llm"
                      ? `Generated with ${messageProvenance.modelId}`
                      : "Prepared from Reputely's deterministic fallback template"
                  }
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${
                    messageProvenance.source === "llm"
                      ? "bg-sky-50 text-sky-700 ring-sky-200"
                      : "bg-amber-50 text-amber-700 ring-amber-200"
                  }`}
                >
                  {messageProvenance.source === "llm"
                    ? "AI-generated draft"
                    : "Deterministic fallback"}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="xs"
                variant={
                  messageWasGeneratedForCurrentSelection ? "outline" : "default"
                }
                disabled={!submission.ok || busy || generating}
                onClick={generateMessage}
              >
                {generating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {generating
                  ? "Generating"
                  : messageWasGeneratedForCurrentSelection
                    ? "Regenerate message"
                    : "Generate message"}
              </Button>
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={!submission.ok || !messageIsCurrent || busy || generating}
                onClick={() => copyField("message", messageDraft.trim())}
              >
                {copiedField === "message" ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {copiedField === "message" ? "Copied" : "Copy message"}
              </Button>
            </div>
          </div>
          <Textarea
            id={`message-${formId}`}
            value={messageDraft}
            onChange={(event) => setMessageDraft(event.target.value)}
            rows={12}
            placeholder={
              generating
                ? "Generating one shared message…"
                : "Generate a draft from the selected review evidence."
            }
            disabled={
              !submission.ok ||
              !messageWasGeneratedForCurrentSelection ||
              busy ||
              generating
            }
            className="min-h-56 bg-background text-sm leading-6"
          />
          <p
            className="mt-1 text-xs text-muted-foreground"
            aria-live="polite"
          >
            {generating
              ? "Generating one shared message from the selected review evidence."
              : messageWasGeneratedForCurrentSelection &&
                  messageProvenance?.source === "deterministic_fallback"
                ? "Model generation was unavailable. Review and edit this fallback draft before copying it to Amazon."
                : messageWasGeneratedForCurrentSelection
                  ? "Review and edit the AI-generated message before copying it to Amazon."
                : "Generate one shared message for the selected reviews. Nothing is sent to Amazon."}
          </p>
        </div>

        <div>
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <label
              htmlFor={`asins-${formId}`}
              className="text-sm font-medium text-foreground"
            >
              List up to 10 ASIN(s) for the product(s) in a comma separated list
            </label>
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={!submission.ok || busy}
              onClick={() =>
                submission.ok &&
                copyField("asins", submission.fields.asinField)
              }
            >
              {copiedField === "asins" ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {copiedField === "asins" ? "Copied" : "Copy ASINs"}
            </Button>
          </div>
          <Input
            id={`asins-${formId}`}
            readOnly
            value={submission.ok ? submission.fields.asinField : ""}
            className="bg-background font-mono"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {submission.ok ? submission.fields.asins.length : 0} of 10 ASINs
          </p>
        </div>

        <div>
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <label
              htmlFor={`urls-${formId}`}
              className="text-sm font-medium text-foreground"
            >
              Provide up to 10 URL(s) in a new line for each product review you want to report
            </label>
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={!submission.ok || busy}
              onClick={() =>
                submission.ok &&
                copyField("urls", submission.fields.reviewUrlsField)
              }
            >
              {copiedField === "urls" ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {copiedField === "urls" ? "Copied" : "Copy review URLs"}
            </Button>
          </div>
          <Textarea
            id={`urls-${formId}`}
            readOnly
            value={submission.ok ? submission.fields.reviewUrlsField : ""}
            rows={Math.max(
              3,
              submission.ok ? submission.fields.reviewUrls.length : 3
            )}
            className="bg-background font-mono text-xs leading-5"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {submission.ok ? submission.fields.reviewUrls.length : 0} of 10
            review URLs
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t border-border pt-4">
          <label className="min-w-44 flex-1 sm:max-w-64">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">
              Amazon case ID (optional)
            </span>
            <Input
              value={amazonCaseId}
              onChange={(event) => setAmazonCaseId(event.target.value)}
              placeholder="Case ID after filing"
              disabled={busy || generating}
            />
          </label>
          <label className="min-w-56 flex-1 sm:max-w-72">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">
              Submitted at
            </span>
            <Input
              type="datetime-local"
              value={submittedAt}
              onChange={(event) => {
                setSubmittedAt(event.target.value);
                setSubmittedAtEdited(true);
              }}
              disabled={busy || generating}
              className="dark:scheme-dark"
            />
            <span className="mt-1 block text-[11px] text-muted-foreground">
              Uses {Intl.DateTimeFormat().resolvedOptions().timeZone}. Edit for
              a backfill; otherwise the button-click time is recorded.
            </span>
          </label>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={!submission.ok || !messageIsCurrent || busy || generating}
              onClick={markBatchSubmitted}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ClipboardCheck className="h-4 w-4" />
              )}
              Record {selectedIds.length || "selected"} submitted
            </Button>
          </div>
        </div>
        {!submission.ok && (
          <p className="text-sm text-destructive" role="alert">
            {submission.error}
          </p>
        )}
        {error && (
          <p className="basis-full text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

function CategoryBatchTools({
  reviews,
  onReload,
}: {
  reviews: QueueReview[];
  onReload: () => void;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const groups = useMemo(
    () => groupQueueReviews(managedFilingQueueReviews(reviews)),
    [reviews]
  );

  function handleSubmitted(nextMessage: string) {
    setMessage(nextMessage);
    onReload();
  }

  return (
    <AdminPanel
      title="Prepare Brand Registry submissions"
      description="Only brands with verified Registered Agent access appear here. Generate and review one shared message, then paste it with the ASINs and review URLs into Brand Registry Support."
    >
      {groups.length === 0 ? (
        <div className="px-4 py-5 text-sm text-muted-foreground">
          No takedown-ready Brand Registry reviews are available to prepare.
        </div>
      ) : (
        <div className="space-y-3 p-3">
          {message && (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 ring-1 ring-inset ring-emerald-200">
              {message}
            </p>
          )}
          {groups.map((group) => (
            <CategoryBatchCard
              key={group.key}
              group={group}
              onSubmitted={handleSubmitted}
            />
          ))}
        </div>
      )}
    </AdminPanel>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <label>
      <span className="mb-1 block text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 w-full rounded-lg border border-input bg-background px-2.5 text-sm outline-none transition-colors focus:border-ring focus:ring-3 focus:ring-ring/50"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function SortableHead({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: QueueSortKey;
  sort: QueueSort;
  onSort: (key: QueueSortKey) => void;
  align?: "left" | "right";
}) {
  const active = sort.key === sortKey;
  const ariaSort = active
    ? sort.direction === "asc"
      ? ("ascending" as const)
      : ("descending" as const)
    : undefined;

  return (
    <TableHead scope="col" aria-sort={ariaSort} className="p-0">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`group inline-flex h-10 w-full min-w-0 cursor-pointer items-center gap-1.5 px-2 font-medium transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
          align === "right" ? "justify-end text-right" : "justify-start text-left"
        }`}
      >
        <span className="truncate">{label}</span>
        {active ? (
          sort.direction === "asc" ? (
            <ArrowUp aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <ArrowDown aria-hidden="true" className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown
            aria-hidden="true"
            className="h-3.5 w-3.5 text-muted-foreground/60 transition-colors group-hover:text-foreground"
          />
        )}
      </button>
    </TableHead>
  );
}

function Inner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeQuery = useMemo(
    () => queryFromFilters(filtersFromParams(searchParams)),
    [searchParams]
  );
  const [filters, setFilters] = useState<FilterState>(() =>
    filtersFromParams(searchParams)
  );
  const [reviews, setReviews] = useState<QueueReview[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [sort, setSort] = useState<QueueSort>(DEFAULT_QUEUE_SORT);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    setFilters(filtersFromParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    setReviews(null);
    setError(null);
    api<QueueResponse>(`/admin/queue${activeQuery ? `?${activeQuery}` : ""}`).then(
      (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setError(res.error ?? "Failed to load queue.");
          return;
        }
        setReviews(res.data?.reviews ?? []);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [reloadKey, activeQuery]);

  function applyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSearchParams(queryFromFilters(filters));
  }

  function resetFilters() {
    setFilters({
      asins: "",
      brand: "",
      email: "",
      customerStatus: "all",
      rating: "all",
      violation: "",
    });
    setSearchParams("");
  }

  const actionableReviews = useMemo(
    () => (reviews ? actionableQueueReviews(reviews) : null),
    [reviews]
  );
  const batchEligibleReviews = useMemo(
    () =>
      actionableReviews?.filter(
        (review) => review.takedownCapability === "brand_registry_ready"
      ) ?? null,
    [actionableReviews]
  );
  const sortedReviews = useMemo(() => {
    if (!actionableReviews) return [];
    return sortQueueReviews(actionableReviews, sort);
  }, [actionableReviews, sort]);
  const toggleSort = useCallback((key: QueueSortKey) => {
    setSort((current) => nextQueueSort(current, key));
  }, []);

  return (
    <AdminPage
      eyebrow="Work"
      title="Takedown inbox"
      description="Flagged reviews awaiting manual submission. Brand Registry batches are available only when that brand's Registered Agent access is verified."
    >
      <form
        onSubmit={applyFilters}
        className="mb-3 grid grid-cols-1 gap-3 rounded-lg border border-border bg-background p-3 md:grid-cols-6"
      >
        <label className="md:col-span-2">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            ASINs
          </span>
          <Input
            value={filters.asins}
            onChange={(event) =>
              setFilters((current) => ({ ...current, asins: event.target.value }))
            }
            placeholder="B0DTRPRC31, B09XGSPPWQ"
            className="font-mono"
          />
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            Brand
          </span>
          <Input
            value={filters.brand}
            onChange={(event) =>
              setFilters((current) => ({ ...current, brand: event.target.value }))
            }
            placeholder="SKINN"
          />
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            Lead / customer
          </span>
          <Input
            value={filters.email}
            onChange={(event) =>
              setFilters((current) => ({ ...current, email: event.target.value }))
            }
            placeholder="email or id"
          />
        </label>
        <SelectField
          label="Access"
          value={filters.customerStatus}
          options={ACCESS_OPTIONS}
          onChange={(customerStatus) =>
            setFilters((current) => ({ ...current, customerStatus }))
          }
        />
        <SelectField
          label="Rating"
          value={filters.rating}
          options={RATING_OPTIONS}
          onChange={(rating) => setFilters((current) => ({ ...current, rating }))}
        />
        <label className="md:col-span-2">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            Violation contains
          </span>
          <Input
            value={filters.violation}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                violation: event.target.value,
              }))
            }
            placeholder="off-topic, competitor, seller"
          />
        </label>
        <div className="flex items-end gap-2 md:col-span-4">
          <Button type="submit" variant="outline">
            <Search className="h-4 w-4" />
            Apply filters
          </Button>
          <Button type="button" variant="ghost" onClick={resetFilters}>
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          {actionableReviews && (
            <span className="ml-auto self-center text-xs text-muted-foreground">
              {actionableReviews.length} result
              {actionableReviews.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </form>

      {actionableReviews && (
        <CategoryBatchTools
          reviews={batchEligibleReviews ?? []}
          onReload={reload}
        />
      )}

      <AdminPanel>
        {error && <p className="px-4 py-3 text-sm text-destructive">{error}</p>}
        {!reviews && !error && <AdminLoading label="Loading takedown inbox" />}
        {actionableReviews && actionableReviews.length === 0 && (
          <AdminEmptyState
            title="No matching reviews"
            description="No waiting takedown items match the current filters."
          />
        )}
        {actionableReviews && actionableReviews.length > 0 && (
          <Table className="table-fixed">
            <colgroup>
              <col className="w-[9%]" />
              <col className="w-[11%]" />
              <col className="w-[14%]" />
              <col className="w-[7%]" />
              <col className="w-[13%]" />
              <col className="w-[24%]" />
              <col className="w-[11%]" />
              <col className="w-[11%]" />
            </colgroup>
              <TableCaption className="sr-only">
                Flagged reviews awaiting submission. Column headers with buttons
                sort the table.
              </TableCaption>
              <TableHeader>
                <TableRow>
                  <SortableHead
                    label="Takedown"
                    sortKey="access"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHead
                    label="Brand"
                    sortKey="brand"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHead
                    label="ASIN / product"
                    sortKey="asin"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHead
                    label="Rating"
                    sortKey="rating"
                    sort={sort}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableHead
                    label="Issue"
                    sortKey="issue"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHead
                    label="Review"
                    sortKey="review"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHead
                    label="Lead / audit"
                    sortKey="lead"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <TableHead scope="col" className="text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedReviews.map((review) => {
                  const takedown = resolveAmazonReportText(review);
                  const fullReviewText = formatQueueReviewText(review);
                  const issues =
                    actionableViolations(review)
                      .map((violation) => violationLabel(violation.type))
                      .filter(Boolean)
                      .join(", ") || "—";

                  return (
                    <TableRow key={review.id} className="align-top">
                      <TableCell className="overflow-hidden">
                        <TakedownCapabilityPill
                          capability={review.takedownCapability}
                        />
                      </TableCell>
                      <TableCell className="overflow-hidden">
                        <div className="min-w-0">
                          {review.brand ? (
                            <Link
                              to={`/admin/customers/${review.brand.customerId}`}
                              className="block truncate font-medium underline-offset-4 hover:underline"
                              title={review.brand.name}
                            >
                              {review.brand.name}
                            </Link>
                          ) : (
                            <span className="block truncate text-muted-foreground">
                              Unknown
                            </span>
                          )}
                          <div
                            className="mt-0.5 truncate text-xs text-muted-foreground"
                            title={
                              review.customer?.organizationName ??
                              review.brand?.organizationName ??
                              "No customer match"
                            }
                          >
                            {review.customer?.organizationName ??
                              review.brand?.organizationName ??
                              "No customer match"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="overflow-hidden">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <a
                            href={`https://www.amazon.com/dp/${review.asin}`}
                            target="_blank"
                            rel="noreferrer"
                            className="truncate font-mono text-xs font-medium underline-offset-4 hover:underline"
                            title={review.asin}
                          >
                            {review.asin}
                          </a>
                          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </div>
                        <div
                          className="mt-1 truncate text-xs text-muted-foreground"
                          title={review.productTitle ?? "Product title unavailable"}
                        >
                          {review.productTitle ?? "Product title unavailable"}
                        </div>
                      </TableCell>
                      <TableCell className="overflow-hidden text-right tabular-nums">
                        {review.rating}★
                      </TableCell>
                      <TableCell className="overflow-hidden text-xs text-muted-foreground">
                        <span className="block truncate" title={issues}>
                          {issues}
                        </span>
                      </TableCell>
                      <TableCell className="overflow-hidden whitespace-normal">
                        <p
                          className="truncate text-sm leading-5 text-foreground/80"
                          title={fullReviewText}
                        >
                          {fullReviewText || "No review text"}
                        </p>
                        {review.classification.rationale && (
                          <p
                            className="mt-1 truncate text-xs leading-5 text-muted-foreground"
                            title={review.classification.rationale}
                          >
                            {review.classification.rationale}
                          </p>
                        )}
                        {takedown.text && (
                          <details className="mt-1 min-w-0">
                            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                              Takedown text ·{" "}
                              {takedown.source === "stored_draft"
                                ? "saved draft"
                                : "fallback draft"}
                            </summary>
                            <pre className="mt-2 max-h-56 max-w-full overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-3 font-sans text-xs text-foreground">
                              {takedown.text}
                            </pre>
                          </details>
                        )}
                      </TableCell>
                      <TableCell className="overflow-hidden">
                        <Link
                          to={`/admin/audits/${review.auditRequest.id}`}
                          className="block truncate text-xs font-medium underline-offset-4 hover:underline"
                          title={review.auditRequest.email}
                        >
                          {review.auditRequest.email}
                        </Link>
                        <div
                          className="mt-1 truncate font-mono text-[11px] text-muted-foreground"
                          title={`${review.auditRequest.id} · ${review.auditRequest.createdAt}`}
                        >
                          {review.auditRequest.id.slice(0, 10)} ·{" "}
                          {review.auditRequest.createdAt.slice(0, 10)}
                        </div>
                      </TableCell>
                      <TableCell className="overflow-hidden">
                        <div className="flex justify-end gap-1">
                          {review.takedownCapability ===
                          "brand_registry_ready" ? (
                            <>
                              <CopyButton text={takedown.text} />
                              <MarkSubmittedButton
                                reviewId={review.id}
                                onChange={reload}
                              />
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Not eligible
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
        )}
      </AdminPanel>
    </AdminPage>
  );
}

export default function AdminQueue() {
  return (
    <AdminGate>
      <AdminLayout>
        <Inner />
      </AdminLayout>
    </AdminGate>
  );
}
